/**
 * What each role may do, and which parts of the app it can see.
 *
 * Both catalogues come from the server. A copy kept here would drift the day
 * an action is added, and the failure is quiet: a checkbox for something the
 * server does not honour, or a permission granted that no box can express.
 *
 * ## Defaults, and why they are drawn as ticks
 *
 * A stored entry for a role *replaces* that role's defaults rather than adding
 * to them:
 *
 *     if let Some(custom) = role_actions.get(role) { return custom.clone(); }
 *     default_actions_for_role(role)
 *
 * With nothing stored, a manager holds thirteen actions. The first version of
 * this screen drew every box unticked, because it showed what was stored
 * rather than what was in force, so ticking one box would have written
 * `{manager: [that one]}` and cut them from thirteen to one without a word.
 *
 * So a role with nothing stored is drawn with its defaults ticked and labelled
 * as such, and saving is described as what it is: pinning the current picture.
 *
 * `admin` and `super_admin` are not editable because they always get
 * everything, and `unapproved` because it gets nothing.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Group, Loader, Stack, Table, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as gw from '../../api/gateway';
import { useEnums } from '../../api/queries';
import { roleLabel, useVocab, type Vocab } from '../../vocab';

/** "edit_campaigns" reads better as "Edit campaigns" in a table of checkboxes. */
const label = (key: string) =>
  key.replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** An action or sidebar key as a person reads it, in the organisation's words. */
export function permissionLabel(v: Vocab, key: string): string {
  switch (key) {
    case 'edit_campaigns': return `Edit ${v.many('campaign')}`;
    case 'edit_destinations': return `Edit ${v.many('destination')}`;
    case 'edit_buyers': return `Edit ${v.many('buyer')}`;
    case 'edit_suppliers': return `Edit ${v.many('supplier')}`;
    case 'edit_leads': return `Edit ${v.many('lead')}`;
    case 'delete_leads': return `Delete ${v.many('lead')}`;
    case 'redistribute_leads': return `Redistribute ${v.many('lead')}`;
    case 'leads': return v.Many('lead');
    case 'campaigns': return v.Many('campaign');
    case 'destinations': return v.Many('destination');
    case 'buyers': return v.Many('buyer');
    case 'suppliers': return v.Many('supplier');
    case 'reports': return v.p.reports;
    case 'test-leads': return `Test ${v.many('lead')}`;
    case 'companies': return v.Many('company');
    case 'deals': return v.Many('deal');
    case 'edit_deals': return `Edit ${v.many('deal')}`;
    case 'edit_companies': return `Edit ${v.many('company')}`;
    case 'manage_pipelines': return `Manage ${v.many('pipeline')}`;
    case 'tasks': return v.Many('task');
    case 'edit_activities': return `Edit ${v.many('note')} and ${v.many('task')}`;
    case 'people': return v.Many('person');
    case 'link_people': return `Merge and unlink ${v.many('person')}`;
    case 'manage_teams': return `Manage ${v.many('team')} and rotations`;
    case 'assign_records': return `Assign ${v.many('owner')}`;
    case 'manage_properties': return `Manage ${v.many('property')}`;
    case 'manage_scoring': return 'Manage scoring rules';
    case 'manage_workflows': return `Manage ${v.many('workflow')}`;
    case 'manage_returns': return `Return ${v.many('lead')} and decide ${v.one('buyer')} returns`;
    case 'manage_billing': return 'Statements and billing details';
    default: return label(key);
  }
}

export function PermissionsTab({ orgId }: { orgId: string | null }) {
  const v = useVocab();
  const qc = useQueryClient();
  const enums = useEnums();
  const perms = useQuery({
    queryKey: ['role-permissions', orgId],
    queryFn: () => gw.rolePermissions(orgId!),
    enabled: !!orgId,
  });

  const roles = enums.data?.configurable_roles ?? [];
  const allActions = enums.data?.actions ?? [];
  const allTabs = enums.data?.nav_tabs ?? [];

  /* Held locally while being edited, so a table of checkboxes is one save
     rather than one request per click. */
  const [actions, setActions] = useState<Record<string, string[]>>({});
  const [navTabs, setNavTabs] = useState<Record<string, string[]>>({});
  const [touched, setTouched] = useState(false);

  /** What a role holds today: its stored override, or its defaults. */
  const inForce = (
    stored: Record<string, string[]> | undefined,
    pick: (d: { role: string; actions: string[]; nav_tabs: string[] }) => string[],
  ): Record<string, string[]> => {
    const out: Record<string, string[]> = {};
    for (const d of enums.data?.default_permissions ?? []) out[d.role] = pick(d);
    for (const [role, held] of Object.entries(stored ?? {})) out[role] = held;
    return out;
  };

  useEffect(() => {
    if (!perms.data || !enums.data) return;
    setActions(inForce(perms.data.role_actions, (d) => d.actions));
    setNavTabs(inForce(perms.data.role_permissions, (d) => d.nav_tabs));
    setTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perms.data, enums.data]);

  /** Roles running on defaults, so the screen can say so. */
  const onDefaults = roles.filter((r) => !(perms.data?.role_actions ?? {})[r]);

  /**
   * Put back anything stored that this screen cannot show.
   *
   * The saved config still names six tabs from the email subsystem deleted in
   * July. They are not in the catalogue, so no checkbox represents them, and
   * saving the visible state alone would quietly delete them the first time
   * anybody ticked an unrelated box. Whether they should still be there is a
   * separate question from whether a checkbox click should decide it.
   */
  const withHidden = (
    edited: Record<string, string[]>,
    stored: Record<string, string[]> | undefined,
    catalogue: string[],
  ): Record<string, string[]> => {
    const out: Record<string, string[]> = { ...edited };
    for (const [role, held] of Object.entries(stored ?? {})) {
      const unseen = held.filter((k) => !catalogue.includes(k));
      if (unseen.length) out[role] = [...(out[role] ?? []), ...unseen];
    }
    return out;
  };

  const save = useMutation({
    mutationFn: () =>
      gw.setRolePermissions(orgId!, {
        role_actions: withHidden(actions, perms.data?.role_actions, allActions),
        role_permissions: withHidden(navTabs, perms.data?.role_permissions, allTabs),
      }),
    onSuccess: () => {
      setTouched(false);
      qc.invalidateQueries({ queryKey: ['role-permissions', orgId] });
      notifications.show({
        color: 'teal',
        title: 'Saved',
        message: 'People take on the new permissions at their next sign in.',
      });
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
  });

  function toggle(
    map: Record<string, string[]>,
    setMap: (m: Record<string, string[]>) => void,
    role: string,
    key: string,
  ) {
    const held = map[role] ?? [];
    const next = held.includes(key) ? held.filter((k) => k !== key) : [...held, key];
    setMap({ ...map, [role]: next });
    setTouched(true);
  }

  if (perms.isPending || enums.isPending) return <Loader size="sm" />;

  return (
    <Stack>
      <Alert color="gray" variant="light">
        <Text size="sm">
          Admins and super admins always have everything, and an unapproved account has
          nothing, so none of them appear here. A change takes effect the next time somebody
          signs in.
        </Text>
        {onDefaults.length > 0 && (
          <Text size="sm" mt={6}>
            {onDefaults.map((r) => roleLabel(v, r)).join(', ')}{' '}
            {onDefaults.length === 1 ? 'is' : 'are'} on the standard permissions for{' '}
            {onDefaults.length === 1 ? 'that role' : 'those roles'}, shown ticked below.
            Saving pins whatever is ticked, so those roles stop following the standard set
            and keep exactly what you leave here.
          </Text>
        )}
      </Alert>

      <Grid
        title="What each role can do"
        roles={roles}
        keys={allActions}
        map={actions}
        onToggle={(role, key) => toggle(actions, setActions, role, key)}
      />

      <Grid
        title="What each role can see"
        subtitle="The items in the sidebar."
        roles={roles}
        keys={allTabs}
        map={navTabs}
        onToggle={(role, key) => toggle(navTabs, setNavTabs, role, key)}
      />

      <Group justify="flex-end">
        <Button loading={save.isPending} disabled={!touched} onClick={() => save.mutate()}>
          Save permissions
        </Button>
      </Group>
    </Stack>
  );
}

function Grid({
  title,
  subtitle,
  roles,
  keys,
  map,
  onToggle,
}: {
  title: string;
  subtitle?: string;
  roles: string[];
  keys: string[];
  map: Record<string, string[]>;
  onToggle: (role: string, key: string) => void;
}) {
  const v = useVocab();
  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="xs">
        <Text fw={600}>{title}</Text>
        {subtitle && <Text size="sm" c="dimmed">{subtitle}</Text>}
        <Table striped withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              {roles.map((r) => (
                <Table.Th key={r} style={{ textAlign: 'center' }}>{roleLabel(v, r)}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {keys.map((k) => (
              <Table.Tr key={k}>
                <Table.Td><Text size="sm">{permissionLabel(v, k)}</Text></Table.Td>
                {roles.map((r) => (
                  <Table.Td key={r} style={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={(map[r] ?? []).includes(k)}
                      onChange={() => onToggle(r, k)}
                      aria-label={`${permissionLabel(v, k)} for ${roleLabel(v, r)}`}
                    />
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}
